import React, { ReactNode, useContext, useMemo } from 'react';
import { usePreviewVolumeStore } from '@/stores/volumePreviewStore';

export enum RemotionUsage {
  PLAYER = 'PLAYER',
  THUMBNAIL = 'THUMBNAIL'
}

/**
 * These values will be used during generation of final output
 * It is expected that context may not be available during downnload due to which
 * following default will be used
 */
const RemotionUsageContextDefaultValues = {
  usage: RemotionUsage.PLAYER,
  isSharedPage: false,
  useNewVideoTag: false,
  isPlayRangeMicroCutsEnabled: false,
  isUploadsPreviewAudioDiscontinuityEnabled: false,
  previewVolume: undefined as ReturnType<typeof usePreviewVolumeStore>
};

/**
 * It is expected during generation of final download this context may not be available
 * Program will not crash because of default values
 */
const RemotionUsageContext = React.createContext(RemotionUsageContextDefaultValues);

export const RemotionUsageContextProvider = ({
  children,
  usage = RemotionUsage.PLAYER,
  isSharedPage = false,
  useNewVideoTag,
  isPlayRangeMicroCutsEnabled = false,
  isUploadsPreviewAudioDiscontinuityEnabled = false,
  previewVolume
}: {
  children: ReactNode | ReactNode[];
  usage?: RemotionUsage;
  isSharedPage?: boolean;
  useNewVideoTag: boolean;
  isPlayRangeMicroCutsEnabled?: boolean;
  isUploadsPreviewAudioDiscontinuityEnabled?: boolean;
  previewVolume?: ReturnType<typeof usePreviewVolumeStore>;
}) => {
  const value = useMemo(() => {
    return {
      usage,
      isSharedPage,
      useNewVideoTag,
      isPlayRangeMicroCutsEnabled,
      isUploadsPreviewAudioDiscontinuityEnabled,
      previewVolume
    };
  }, [
    usage,
    isSharedPage,
    useNewVideoTag,
    isPlayRangeMicroCutsEnabled,
    isUploadsPreviewAudioDiscontinuityEnabled,
    previewVolume
  ]);

  return <RemotionUsageContext.Provider value={value}>{children}</RemotionUsageContext.Provider>;
};
/**
 * Note this can be called even if we're not actually inside the context
 * In that case default values should be returned
 */
export const useRemotionUsage = () => {
  return useContext(RemotionUsageContext);
};
